// Long-press drag-and-drop for list rows, built on Pointer Events (the HTML5
// drag-and-drop API is unreliable on touch). A row opts in by carrying
// `data-drag-id` and `data-drag-kind` ("item" | "group"); the controller is
// attached once to the list container and works by delegation, so it survives
// the rows being re-rendered.
//
// Two modes, picked by which callback is passed:
//   onDrop    — drop semantics: release over another row to act on the pair
//               (e.g. make/join a group, nest a group). The default.
//   onReorder — reorder semantics: the dragged row slides among its siblings
//               live, and on release onReorder(orderedIds) gets the new order of
//               the container's direct `data-drag-id` children. Use for ranking
//               a list in place.
//
// Gesture: press and hold a row (~longPressMs) to "lift" it into drag mode — a
// floating ghost follows the pointer. Release to drop/commit; release after only
// a tap (no hold) leaves the row's normal click (navigation) intact; moving
// before the hold completes reads as a scroll and cancels. A completed lift
// suppresses the click that would otherwise follow.

export function enableRowDrag(
	container,
	{ onDrop, onReorder, longPressMs = 400, moveCancelPx = 10 },
) {
	let state = null;
	let suppressClick = false;
	const reorder = typeof onReorder === "function";

	const rowFrom = (node) => node?.closest?.("[data-drag-id]") ?? null;

	// A dragged group can only nest into another group; a dragged item can land on
	// an item (create a group) or a group (join it).
	function isValidTarget(targetRow) {
		if (!targetRow || targetRow === state.sourceEl) return false;
		if (state.sourceKind === "group") {
			return targetRow.dataset.dragKind === "group";
		}
		return true;
	}

	function positionGhost(x, y) {
		if (!state?.ghost) return;
		state.ghost.style.left = `${x - state.offsetX}px`;
		state.ghost.style.top = `${y - state.offsetY}px`;
	}

	function lift() {
		state.lifted = true;
		navigator.vibrate?.(15);

		const rect = state.sourceEl.getBoundingClientRect();
		const ghost = state.sourceEl.cloneNode(true);
		ghost.classList.add("drag-ghost");
		ghost.removeAttribute("data-drag-id");
		Object.assign(ghost.style, {
			position: "fixed",
			left: `${rect.left}px`,
			top: `${rect.top}px`,
			width: `${rect.width}px`,
			height: `${rect.height}px`,
			margin: "0",
			pointerEvents: "none",
			zIndex: "9999",
		});
		document.body.appendChild(ghost);

		state.ghost = ghost;
		state.offsetX = state.startX - rect.left;
		state.offsetY = state.startY - rect.top;
		state.sourceEl.classList.add("drag-source");
		document.body.classList.add("dragging-rows");
		positionGhost(state.startX, state.startY);
	}

	// Block page scroll while dragging. This listener is registered at
	// pointerdown (NOT at lift): iOS Safari fixes a touch's scrollability when the
	// touch begins, so a non-passive touchmove handler added mid-gesture is
	// ignored and the page scrolls anyway (cancelling the drag). Registered early,
	// it only actually preventDefaults once we've lifted.
	function preventTouch(e) {
		if (state?.lifted) e.preventDefault();
	}

	// Kill the native long-press menu (Android context menu / iOS callout) that
	// would otherwise interrupt the hold.
	function preventContextMenu(e) {
		if (state) e.preventDefault();
	}

	function setTarget(row) {
		if (state.currentTarget === row) return;
		state.currentTarget?.classList.remove("drag-over");
		state.currentTarget = row;
		row?.classList.add("drag-over");
	}

	// Reorder mode: slide the (dimmed) source row among its siblings to sit on the
	// side of the row under the pointer that the pointer is closest to. The DOM is
	// the source of truth for the new order, read back on release.
	function reorderMove(x, y) {
		state.ghost.style.visibility = "hidden";
		const under = document.elementFromPoint(x, y);
		state.ghost.style.visibility = "";
		const targetRow = rowFrom(under);
		if (!targetRow || targetRow === state.sourceEl) return;
		const parent = state.sourceEl.parentNode;
		if (!parent || targetRow.parentNode !== parent) return;
		const rect = targetRow.getBoundingClientRect();
		const before = y < rect.top + rect.height / 2;
		if (before) {
			if (targetRow.previousSibling !== state.sourceEl) {
				parent.insertBefore(state.sourceEl, targetRow);
			}
		} else if (targetRow.nextSibling !== state.sourceEl) {
			parent.insertBefore(state.sourceEl, targetRow.nextSibling);
		}
	}

	// The current order of the container's direct draggable children.
	function currentOrder() {
		return [...container.querySelectorAll(":scope > [data-drag-id]")].map(
			(n) => n.dataset.dragId,
		);
	}

	function onPointerDown(e) {
		if (state) return;
		if (e.pointerType === "mouse" && e.button !== 0) return;
		const row = rowFrom(e.target);
		if (!row) return;
		state = {
			sourceEl: row,
			sourceId: row.dataset.dragId,
			sourceKind: row.dataset.dragKind,
			startX: e.clientX,
			startY: e.clientY,
			pointerId: e.pointerId,
			lifted: false,
			ghost: null,
			currentTarget: null,
			timer: setTimeout(lift, longPressMs),
		};
		// Route this touch's events to the row so they keep flowing as the finger
		// moves over other rows (and survive re-renders). Best-effort.
		try {
			row.setPointerCapture(e.pointerId);
		} catch {
			/* not supported / already released — fine */
		}
		window.addEventListener("pointermove", onPointerMove, { passive: false });
		window.addEventListener("pointerup", onPointerUp);
		window.addEventListener("pointercancel", onPointerUp);
		// Registered now (not at lift) so iOS honours the scroll-block — see
		// preventTouch. Also suppress the long-press menu for the whole press.
		window.addEventListener("touchmove", preventTouch, { passive: false });
		window.addEventListener("contextmenu", preventContextMenu);
	}

	function onPointerMove(e) {
		if (!state) return;
		if (!state.lifted) {
			// Movement before the hold completes means the user is scrolling — bail
			// and let the native scroll happen.
			if (Math.hypot(e.clientX - state.startX, e.clientY - state.startY) > moveCancelPx) {
				cleanup();
			}
			return;
		}
		e.preventDefault();
		positionGhost(e.clientX, e.clientY);
		if (reorder) {
			reorderMove(e.clientX, e.clientY);
			return;
		}
		// Hide the ghost for the hit-test so we read the row underneath, not it.
		state.ghost.style.visibility = "hidden";
		const under = document.elementFromPoint(e.clientX, e.clientY);
		state.ghost.style.visibility = "";
		const targetRow = rowFrom(under);
		setTarget(isValidTarget(targetRow) ? targetRow : null);
	}

	async function onPointerUp() {
		if (!state) return;
		const lifted = state.lifted;
		// Reorder commits the live DOM order; drop builds a source/target pair.
		const orderedIds = reorder && lifted ? currentOrder() : null;
		const payload =
			!reorder && lifted && state.currentTarget
				? {
						sourceId: state.sourceId,
						sourceKind: state.sourceKind,
						targetId: state.currentTarget.dataset.dragId,
						targetKind: state.currentTarget.dataset.dragKind,
					}
				: null;
		if (lifted) {
			// A click synthesised after the drag would re-trigger navigation; swallow
			// the next one.
			suppressClick = true;
			setTimeout(() => {
				suppressClick = false;
			}, 350);
		}
		cleanup();
		try {
			if (orderedIds) await onReorder(orderedIds);
			else if (payload) await onDrop(payload);
		} catch (err) {
			console.error("drag drop failed", err);
		}
	}

	function cleanup() {
		if (!state) return;
		clearTimeout(state.timer);
		window.removeEventListener("pointermove", onPointerMove);
		window.removeEventListener("pointerup", onPointerUp);
		window.removeEventListener("pointercancel", onPointerUp);
		window.removeEventListener("touchmove", preventTouch);
		window.removeEventListener("contextmenu", preventContextMenu);
		try {
			state.sourceEl.releasePointerCapture(state.pointerId);
		} catch {
			/* already released — fine */
		}
		state.currentTarget?.classList.remove("drag-over");
		state.sourceEl.classList.remove("drag-source");
		state.ghost?.remove();
		document.body.classList.remove("dragging-rows");
		state = null;
	}

	function onClickCapture(e) {
		if (suppressClick) {
			e.preventDefault();
			e.stopPropagation();
			suppressClick = false;
		}
	}

	container.addEventListener("pointerdown", onPointerDown);
	container.addEventListener("click", onClickCapture, true);

	return function dispose() {
		container.removeEventListener("pointerdown", onPointerDown);
		container.removeEventListener("click", onClickCapture, true);
		cleanup();
	};
}
